import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { calculateImposition } from '@/utils/imposition-calculator';
import { useDebounce } from '@/hooks/useDebounce';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Calculator,
  Layers,
  Sparkles,
  Printer,
  Scissors,
  Box,
  FileText,
  Loader2,
  CheckCircle2,
  Maximize2,
  RotateCw,
  Compass,
  Package,
  Wand2,
  Settings2,
  Search,
  Check,
  ChevronsUpDown,
  Zap,
  ListChecks,
  Plus,
  Trash2,
  Component,
  FolderKanban,
  ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';

export interface EstimatingComponent {
  id: string;
  name: string;
  partType: 'COVER' | 'BODY' | 'TRAY' | 'SLEEVE' | 'CUSTOM';
  flatWidth: number;
  flatLength: number;
  manualUps?: number | null;
  sheetWidth?: number | null;
  sheetLength?: number | null;
  grainDirection: 'MD' | 'CD' | 'AUTO';
  isRotated90: boolean;
  boxDimensions: { length: number; width: number; height: number };
  boxPresetCode: string;
  // Giấy mộc & In
  paperType: string;
  gsm: number;
  paperPriceKg: number;
  printScheme: 'SINGLE_SIDED' | 'WORK_AND_TURN' | 'WORK_AND_TUMBLE' | 'SHEETWISE_AB';
  colorsFront: number;
  spotColorsFront: number;
  colorsBack: number;
  spotColorsBack: number;
  // Gia công sau in của thành phần
  laminationSides: 'NONE' | 'ONE_SIDE' | 'TWO_SIDES';
  laminationType: 'BOPP_MATTE' | 'BOPP_GLOSS' | 'SOFT_TOUCH' | 'THERMAL' | 'METALLIZED';
  mountingType: 'NONE' | 'FLUTE_E' | 'FLUTE_B' | 'FLUTE_F' | 'DUPLEX_MOUNT' | 'RIGID_BOARD';
  foilGold: boolean;
  foilSilver: boolean;
  foilHolo: boolean;
  foilRoseGold: boolean;
  foilAreaCm2: number;
  hasSpotUv: boolean;
  hasUvTexture: boolean;
  hasVarnish: boolean;
  hasEmbossing: boolean;
  hasDebossing: boolean;
  diecutType: 'NONE' | 'STANDARD' | 'DEMI' | 'PERFORATION';
  hasAutoStripping: boolean;
}

export default function MachineTradeoffSimulator() {
  const queryClient = useQueryClient();

  // ── 1. GLOBAL STATE SẢN LƯỢNG & ĐA THÀNH PHẦN ──
  const [orderQty, setOrderQty] = useState<number>(7000);
  const [activeImpositionView, setActiveImpositionView] = useState<'SMALL' | 'LARGE'>('SMALL');

  // Multi-components state
  const [components, setComponents] = useState<EstimatingComponent[]>([
    {
      id: 'comp-cover',
      name: 'Bìa Cứng Hộp',
      partType: 'COVER',
      flatWidth: 260,
      flatLength: 380,
      grainDirection: 'MD',
      isRotated90: false,
      boxDimensions: { length: 120, width: 80, height: 40 },
      boxPresetCode: 'ECMA_A20_10',
      paperType: 'Couche',
      gsm: 150,
      paperPriceKg: 29000,
      printScheme: 'WORK_AND_TURN',
      colorsFront: 4,
      spotColorsFront: 1,
      colorsBack: 0,
      spotColorsBack: 0,
      laminationSides: 'ONE_SIDE',
      laminationType: 'SOFT_TOUCH',
      mountingType: 'RIGID_BOARD',
      foilGold: true,
      foilSilver: false,
      foilHolo: false,
      foilRoseGold: false,
      foilAreaCm2: 30,
      hasSpotUv: true,
      hasUvTexture: false,
      hasVarnish: false,
      hasEmbossing: true,
      hasDebossing: false,
      diecutType: 'STANDARD',
      hasAutoStripping: true,
    },
    {
      id: 'comp-tray',
      name: 'Khay Lót Định Hình',
      partType: 'TRAY',
      flatWidth: 210,
      flatLength: 280,
      grainDirection: 'MD',
      isRotated90: false,
      boxDimensions: { length: 115, width: 75, height: 35 },
      boxPresetCode: 'ECMA_A20_10',
      paperType: 'Ivory',
      gsm: 350,
      paperPriceKg: 28000,
      printScheme: 'SINGLE_SIDED',
      colorsFront: 1,
      spotColorsFront: 0,
      colorsBack: 0,
      spotColorsBack: 0,
      laminationSides: 'NONE',
      laminationType: 'BOPP_MATTE',
      mountingType: 'NONE',
      foilGold: false,
      foilSilver: false,
      foilHolo: false,
      foilRoseGold: false,
      foilAreaCm2: 0,
      hasSpotUv: false,
      hasUvTexture: false,
      hasVarnish: false,
      hasEmbossing: false,
      hasDebossing: false,
      diecutType: 'STANDARD',
      hasAutoStripping: true,
    },
  ]);

  const [activeComponentId, setActiveComponentId] = useState<string>('comp-cover');

  // Lắp ráp chung & Đóng gói
  const [assemblyCostPerUnit, setAssemblyCostPerUnit] = useState<number>(1200);
  const [glueType, setGlueType] = useState<'AUTO_LOCK_BOTTOM' | 'SIDE_SEAM' | 'SNAP_BOTTOM' | 'FOUR_CORNER' | 'MANUAL'>('AUTO_LOCK_BOTTOM');
  const [hasWindowPatching, setHasWindowPatching] = useState<boolean>(false);
  const [packagingType, setPackagingType] = useState<'CARTON_3L' | 'CARTON_5L' | 'PE_BAG' | 'PALLET'>('CARTON_5L');

  // Tài chính
  const [marginPercent, setMarginPercent] = useState<number>(20);
  const [overheadPercent, setOverheadPercent] = useState<number>(10);

  // Popover states
  const [isPresetOpen, setIsPresetOpen] = useState(false);
  const [isMaterialOpen, setIsMaterialOpen] = useState(false);
  const [isProcessMatrixOpen, setIsProcessMatrixOpen] = useState(false);

  // 1-Click Quotation Modal
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [targetMachineKey, setTargetMachineKey] = useState<'SMALL' | 'LARGE'>('SMALL');
  const [targetOrderQty, setTargetOrderQty] = useState<number>(7000);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [customProductName, setCustomProductName] = useState<string>('Bộ Hộp Cứng Quà Tặng Cao Cấp (Bìa + Khay)');
  const [quoteNotes, setQuoteNotes] = useState<string>('');

  // ── 2. FETCH PRESETS, MATERIALS, CUSTOMERS & PRICING CONFIGS ──
  const { data: configsData } = useQuery({
    ...trpc.pricingConfig.getAllConfigs.queryOptions(),
  });
  const machines = (configsData?.machines || []) as any[];
  const isSingleMachine = machines.length <= 1;
  const primaryMachine = machines.find((m: any) => m.code === 'OFFSET_72X102') || machines[0] || {
    code: 'OFFSET_72X102',
    name: 'Máy in Offset 4 màu khổ 72x102',
    maxSheetWidth: 720,
    maxSheetLength: 1020,
    cmykBatchCost: 1200000,
    runOnCostPerUnit: 150,
    plateCostPerPlate: 120000,
    batchSize: 1000,
  };

  const { data: boxPresets = [] } = useQuery({
    ...trpc.estimating.getBoxPresets.queryOptions(),
  });

  const { data: materialsData } = useQuery({
    ...trpc.materials.list.queryOptions({ limit: 100 }),
  });
  const materialList = ((materialsData as any)?.items || materialsData || []) as any[];

  const { data: customersData } = useQuery({
    ...trpc.customers.list.queryOptions({ limit: 100 }),
  });
  const customerList = ((customersData as any)?.items || customersData || []) as any[];

  // Active component helper
  const activeComp = components.find(c => c.id === activeComponentId) || components[0];

  const updateActiveComponent = (fields: Partial<EstimatingComponent>) => {
    setComponents(prev => prev.map(c => (c.id === activeComp.id ? { ...c, ...fields } : c)));
  };

  const handleAddComponent = () => {
    const newId = 'comp-' + Date.now();
    const newComp: EstimatingComponent = {
      id: newId,
      name: `Chi Tiết #${components.length + 1}`,
      partType: 'CUSTOM',
      flatWidth: 200,
      flatLength: 280,
      grainDirection: 'MD',
      isRotated90: false,
      boxDimensions: { length: 100, width: 60, height: 30 },
      boxPresetCode: 'ECMA_A20_10',
      paperType: 'Ivory',
      gsm: 300,
      paperPriceKg: 28000,
      printScheme: 'SINGLE_SIDED',
      colorsFront: 4,
      spotColorsFront: 0,
      colorsBack: 0,
      spotColorsBack: 0,
      laminationSides: 'NONE',
      laminationType: 'BOPP_MATTE',
      mountingType: 'NONE',
      foilGold: false,
      foilSilver: false,
      foilHolo: false,
      foilRoseGold: false,
      foilAreaCm2: 0,
      hasSpotUv: false,
      hasUvTexture: false,
      hasVarnish: false,
      hasEmbossing: false,
      hasDebossing: false,
      diecutType: 'STANDARD',
      hasAutoStripping: true,
    };
    setComponents(prev => [...prev, newComp]);
    setActiveComponentId(newId);
    toast.success('Đã thêm thành phần mới');
  };

  const handleDeleteComponent = (id: string) => {
    if (components.length <= 1) {
      toast.error('Sản phẩm cần tối thiểu 1 thành phần');
      return;
    }
    setComponents(prev => prev.filter(c => c.id !== id));
    if (activeComponentId === id) {
      setActiveComponentId(components[0].id === id ? components[1].id : components[0].id);
    }
    toast.info('Đã xóa thành phần');
  };

  const handleSelectBoxPreset = (code: string) => {
    setIsPresetOpen(false);
    const preset = (boxPresets as any[]).find(p => p.code === code);
    if (preset && preset.defaultDimensions) {
      const def = preset.defaultDimensions;
      let fw = 210;
      let fl = 280;
      if (code === 'ECMA_A20_10') {
        fw = Math.round(2 * (def.length + def.width) + 15 + 1.6);
        fl = Math.round(def.height + def.width + 15 + (def.width / 2 + 10));
      } else if (code === 'ECMA_A20_20') {
        fw = Math.round(2 * (def.length + def.width) + 15 + 1.6);
        fl = Math.round(def.height + 2 * def.width + 18 + 18);
      } else if (code === 'ECMA_C20') {
        fw = Math.round(def.length + 2 * def.height + 30);
        fl = Math.round(def.width + 2 * def.height + 30);
      } else if (code === 'FEFCO_0201') {
        fw = Math.round(2 * (def.length + def.width) + 35);
        fl = Math.round(def.height + def.width);
      }
      updateActiveComponent({
        boxPresetCode: code,
        boxDimensions: { length: def.length, width: def.width, height: def.height },
        flatWidth: fw,
        flatLength: fl,
      });
      toast.info('Đã chọn mẫu: ' + preset.name);
    }
  };

  // ── 3. 1-CLICK WORKFLOW PRESETS ──
  const applyWorkflowPreset = (presetKey: 'COSMETIC' | 'LUXURY' | 'CORRUGATED' | 'WINDOW') => {
    if (presetKey === 'COSMETIC') {
      updateActiveComponent({
        laminationSides: 'ONE_SIDE',
        laminationType: 'BOPP_MATTE',
        mountingType: 'NONE',
        foilGold: false,
        hasSpotUv: false,
      });
      setGlueType('AUTO_LOCK_BOTTOM');
      setPackagingType('CARTON_3L');
      toast.success('Đã áp dụng: Hộp Mỹ Phẩm Màng Mờ');
    } else if (presetKey === 'LUXURY') {
      updateActiveComponent({
        laminationSides: 'ONE_SIDE',
        laminationType: 'SOFT_TOUCH',
        mountingType: 'RIGID_BOARD',
        foilGold: true,
        hasSpotUv: true,
        hasEmbossing: true,
      });
      setPackagingType('CARTON_5L');
      toast.success('Đã áp dụng: Hộp Quà Luxury Ép Kim & Bồi Bìa Cứng');
    } else if (presetKey === 'CORRUGATED') {
      updateActiveComponent({
        laminationSides: 'ONE_SIDE',
        laminationType: 'BOPP_GLOSS',
        mountingType: 'FLUTE_E',
        foilGold: false,
        hasSpotUv: false,
      });
      setPackagingType('CARTON_5L');
      toast.success('Đã áp dụng: Thùng Sóng E Bồi');
    } else if (presetKey === 'WINDOW') {
      updateActiveComponent({
        laminationSides: 'ONE_SIDE',
        laminationType: 'BOPP_MATTE',
        mountingType: 'NONE',
      });
      setHasWindowPatching(true);
      setPackagingType('CARTON_3L');
      toast.success('Đã áp dụng: Hộp Cửa Sổ Suppo PET');
    }
  };

  // ── 4. MULTI-COMPONENT CALCULATION ENGINE (DEBOUNCED FOR ZERO LAG) ──
  const debouncedComponents = useDebounce(components, 300);
  const debouncedOrderQty = useDebounce(orderQty, 300);

  const simulationData = useMemo(() => {
    // Tính toán chi phí cho từng thành phần dựa trên động cơ bình file chuẩn
    const componentBreakdowns = debouncedComponents.map(comp => {
      const effW = comp.isRotated90 ? comp.flatLength : comp.flatWidth;
      const effL = comp.isRotated90 ? comp.flatWidth : comp.flatLength;

      // 1. Máy nhỏ: 540 x 790 mm (Komori Lithrone 28 / SM74)
      const sImp = calculateImposition(540, 790, comp.flatWidth, comp.flatLength, 15, 3, 5);
      const sCols = Math.max(1, sImp.cols);
      const sRows = Math.max(1, sImp.rows);
      const sUps = Math.max(1, sImp.outs);

      // In tự trở: 1 tờ in cắt đôi được 2 sp -> Tờ in tịnh = SL / (2 * Ups)
      const isWorkAndTurn = comp.printScheme === 'WORK_AND_TURN' || comp.printScheme === 'WORK_AND_TUMBLE';
      const isSheetwiseAB = comp.printScheme === 'SHEETWISE_AB';

      const sNetSheets = isWorkAndTurn ? Math.ceil(debouncedOrderQty / (2 * sUps)) : Math.ceil(debouncedOrderQty / sUps);
      const sGrossSheets = Math.ceil(sNetSheets * 1.18) + 150;
      const sSheetAreaM2 = (0.54 * 0.79);
      const sSheetWeight = (sSheetAreaM2 * comp.gsm) / 1000;
      const sPaperCost = Math.round(sGrossSheets * sSheetWeight * comp.paperPriceKg);

      // Kẽm CTP: Tự trở chỉ tốn số kẽm mặt trước; AB tốn cả 2 mặt
      let sPlateCount = comp.colorsFront + comp.spotColorsFront;
      if (isSheetwiseAB) sPlateCount += (comp.colorsBack + comp.spotColorsBack);
      const sPlateCost = sPlateCount * 120000;

      // Lượt in offset
      const sPrintPasses = isWorkAndTurn || isSheetwiseAB ? 2 : 1;
      const sPrintCost = (800000 * sPrintPasses) + Math.round(sGrossSheets * 90 * sPrintPasses);

      // Gia công thành phần
      let sFinishingCost = 0;
      if (comp.laminationSides !== 'NONE') {
        const lamMul = comp.laminationSides === 'TWO_SIDES' ? 2 : 1;
        let rate = 500;
        if (comp.laminationType === 'BOPP_GLOSS') rate = 450;
        else if (comp.laminationType === 'SOFT_TOUCH') rate = 1400;
        else if (comp.laminationType === 'THERMAL') rate = 900;
        else if (comp.laminationType === 'METALLIZED') rate = 1200;
        sFinishingCost += Math.round(sGrossSheets * sSheetAreaM2 * rate * lamMul + 150000);
      }

      if (comp.mountingType !== 'NONE') {
        let fRate = 3500;
        if (comp.mountingType === 'FLUTE_B') fRate = 3900;
        else if (comp.mountingType === 'FLUTE_F') fRate = 3100;
        else if (comp.mountingType === 'DUPLEX_MOUNT') fRate = 2800;
        else if (comp.mountingType === 'RIGID_BOARD') fRate = 8500;
        sFinishingCost += Math.round(sGrossSheets * (sSheetAreaM2 * fRate + 300) + 300000);
      }

      let foilCount = 0;
      if (comp.foilGold) foilCount++;
      if (comp.foilSilver) foilCount++;
      if (comp.foilHolo) foilCount++;
      if (comp.foilRoseGold) foilCount++;
      if (foilCount > 0) {
        sFinishingCost += (foilCount * (350000 + comp.foilAreaCm2 * 1200)) + Math.round(sGrossSheets * 80 * foilCount + (comp.foilAreaCm2 / 10000) * 8000 * sGrossSheets) + 200000;
      }

      if (comp.hasSpotUv) sFinishingCost += 250000 + Math.round(sGrossSheets * 120);
      if (comp.hasUvTexture) sFinishingCost += 300000 + Math.round(sGrossSheets * 160);
      if (comp.hasVarnish) sFinishingCost += 100000 + Math.round(sGrossSheets * 40);
      if (comp.hasEmbossing) sFinishingCost += 350000 + Math.round(sGrossSheets * 60);
      if (comp.hasDebossing) sFinishingCost += 350000 + Math.round(sGrossSheets * 60);

      const sDieCost = 750000;
      const sDieRunCost = Math.round(sGrossSheets * (comp.hasAutoStripping ? 80 : 110) + 350000);
      sFinishingCost += sDieRunCost;

      const sTotalCompCost = sPaperCost + sPlateCost + sDieCost + sPrintCost + sFinishingCost;

      // 2. Máy lớn: Khổ thực tế từ DB (720 x 1020 mm)
      const lW = primaryMachine.maxSheetWidth || 720;
      const lL = primaryMachine.maxSheetLength || 1020;
      const lImp = calculateImposition(lW, lL, comp.flatWidth, comp.flatLength, 15, 3, 5);
      const lCols = Math.max(1, lImp.cols);
      const lRows = Math.max(1, lImp.rows);
      const lUps = Math.max(1, lImp.outs);

      const lNetSheets = isWorkAndTurn ? Math.ceil(debouncedOrderQty / (2 * lUps)) : Math.ceil(debouncedOrderQty / lUps);
      const lGrossSheets = Math.ceil(lNetSheets * 1.22) + 250;
      const lSheetAreaM2 = (lW * lL) / 1_000_000;
      const lSheetWeight = (lSheetAreaM2 * comp.gsm) / 1000;
      const lPaperCost = Math.round(lGrossSheets * lSheetWeight * comp.paperPriceKg);

      const lPlateRate = Number(primaryMachine.plateCost ?? primaryMachine.plateCostPerPlate ?? 120000);
      const lPlateCost = sPlateCount * lPlateRate;
      const lBatchRate = Number(primaryMachine.cmykBatchCost ?? 1200000);
      const lRunOnRate = Number(primaryMachine.cmykRunOnCost ?? primaryMachine.runOnCostPerUnit ?? 150);
      const lPrintCost = (lBatchRate * sPrintPasses) + Math.round(lGrossSheets * lRunOnRate * sPrintPasses);

      let lFinishingCost = 0;
      if (comp.laminationSides !== 'NONE') {
        const lamMul = comp.laminationSides === 'TWO_SIDES' ? 2 : 1;
        let rate = 500;
        if (comp.laminationType === 'BOPP_GLOSS') rate = 450;
        else if (comp.laminationType === 'SOFT_TOUCH') rate = 1400;
        else if (comp.laminationType === 'THERMAL') rate = 900;
        else if (comp.laminationType === 'METALLIZED') rate = 1200;
        lFinishingCost += Math.round(lGrossSheets * lSheetAreaM2 * rate * lamMul + 200000);
      }

      if (comp.mountingType !== 'NONE') {
        let fRate = 3500;
        if (comp.mountingType === 'FLUTE_B') fRate = 3900;
        else if (comp.mountingType === 'FLUTE_F') fRate = 3100;
        else if (comp.mountingType === 'DUPLEX_MOUNT') fRate = 2800;
        else if (comp.mountingType === 'RIGID_BOARD') fRate = 8500;
        lFinishingCost += Math.round(lGrossSheets * (lSheetAreaM2 * fRate + 300) + 400000);
      }

      if (foilCount > 0) {
        lFinishingCost += (foilCount * (550000 + comp.foilAreaCm2 * 1500)) + Math.round(lGrossSheets * 100 * foilCount + (comp.foilAreaCm2 / 10000) * 8000 * lGrossSheets) + 300000;
      }

      if (comp.hasSpotUv) lFinishingCost += 350000 + Math.round(lGrossSheets * 150);
      if (comp.hasUvTexture) lFinishingCost += 400000 + Math.round(lGrossSheets * 200);
      if (comp.hasVarnish) lFinishingCost += 150000 + Math.round(lGrossSheets * 60);
      if (comp.hasEmbossing) lFinishingCost += 450000 + Math.round(lGrossSheets * 80);
      if (comp.hasDebossing) lFinishingCost += 450000 + Math.round(lGrossSheets * 80);

      const lDieCost = 1400000;
      const lDieRunCost = Math.round(lGrossSheets * (comp.hasAutoStripping ? 80 : 110) + 400000);
      lFinishingCost += lDieRunCost;

      const lTotalCompCost = lPaperCost + lPlateCost + lDieCost + lPrintCost + lFinishingCost;

      return {
        comp,
        effW,
        effL,
        small: {
          ups: sUps,
          cols: sCols,
          rows: sRows,
          netSheets: sNetSheets,
          grossSheets: sGrossSheets,
          paperCost: sPaperCost,
          plateCost: sPlateCost,
          dieCost: sDieCost,
          printCost: sPrintCost,
          finishingCost: sFinishingCost,
          totalCost: sTotalCompCost,
          unitCost: Math.round(sTotalCompCost / orderQty),
        },
        large: {
          ups: lUps,
          cols: lCols,
          rows: lRows,
          netSheets: lNetSheets,
          grossSheets: lGrossSheets,
          paperCost: lPaperCost,
          plateCost: lPlateCost,
          dieCost: lDieCost,
          printCost: lPrintCost,
          finishingCost: lFinishingCost,
          totalCost: lTotalCompCost,
          unitCost: Math.round(lTotalCompCost / orderQty),
        },
      };
    });

    // Chi phí lắp ráp & đóng gói chung
    const glueUnitCost = glueType === 'AUTO_LOCK_BOTTOM' ? 90 : glueType === 'SNAP_BOTTOM' ? 60 : 40;
    const suppoCost = hasWindowPatching ? Math.round(orderQty * 180 + 300000) : 0;
    const packCost = Math.ceil(orderQty / 500) * (packagingType === 'CARTON_5L' ? 35000 : 25000) + orderQty * 15;
    const totalAssemblyCost = (orderQty * assemblyCostPerUnit) + (orderQty * glueUnitCost + 200000) + suppoCost + packCost;

    // Tổng hợp Máy Nhỏ
    const sComponentsTotal = componentBreakdowns.reduce((acc, c) => acc + c.small.totalCost, 0);
    const smallGrandTotal = sComponentsTotal + totalAssemblyCost;
    const smallMargin = Math.round(smallGrandTotal * (marginPercent / 100));
    const smallQuoteTotal = smallGrandTotal + smallMargin;
    const smallUnitPrice = Math.round(smallQuoteTotal / orderQty);

    // Tổng hợp Máy Lớn
    const lComponentsTotal = componentBreakdowns.reduce((acc, c) => acc + c.large.totalCost, 0);
    const largeGrandTotal = lComponentsTotal + totalAssemblyCost;
    const largeMargin = Math.round(largeGrandTotal * (marginPercent / 100));
    const largeQuoteTotal = largeGrandTotal + largeMargin;
    const largeUnitPrice = Math.round(largeQuoteTotal / orderQty);

    const isSmallBetter = isSingleMachine ? false : smallQuoteTotal <= largeQuoteTotal;
    const diff = Math.abs(largeQuoteTotal - smallQuoteTotal);

    // Active component breakdown for SVG display
    const activeBreakdown = componentBreakdowns.find(cb => cb.comp.id === activeComp.id) || componentBreakdowns[0];

    return {
      orderQty,
      componentsCount: components.length,
      componentBreakdowns,
      activeBreakdown,
      totalAssemblyCost,
      recommendedKey: (isSingleMachine || !isSmallBetter) ? ('LARGE' as const) : ('SMALL' as const),
      summaryRecommendation: isSingleMachine
        ? `Phương án tối ưu trên máy in Offset khổ 72x102 (${primaryMachine.name || 'Thiết bị xưởng'}) với ${activeBreakdown.large.ups} con/tờ cho đơn hàng ${orderQty.toLocaleString()} pcs.`
        : (isSmallBetter
          ? `Nên chọn máy khổ nhỏ (54x79cm) để tiết kiệm ${diff.toLocaleString()} đ chi phí kẽm & setup cho SL ${orderQty.toLocaleString()} pcs.`
          : `Nên chọn máy khổ lớn (72x102cm) để tối ưu ${diff.toLocaleString()} đ chi phí dập in hàng loạt cho SL ${orderQty.toLocaleString()} pcs.`),
      plans: {
        small: {
          machineKey: 'SMALL',
          machineName: 'Máy In Offset 4 Màu Komori Lithrone 28 (Mô phỏng)',
          sheetFormat: '540 x 790 mm',
          sheetWidth: 540,
          sheetLength: 790,
          totalCost: smallGrandTotal,
          unitPrice: smallUnitPrice,
          quotationTotal: smallQuoteTotal,
          marginAmount: smallMargin,
          isRecommended: isSingleMachine ? false : isSmallBetter,
          ups: activeBreakdown.small.ups,
          cols: activeBreakdown.small.cols,
          rows: activeBreakdown.small.rows,
          netSheets: activeBreakdown.small.netSheets,
          grossSheets: activeBreakdown.small.grossSheets,
          paperCost: componentBreakdowns.reduce((a, c) => a + c.small.paperCost, 0),
          plateCost: componentBreakdowns.reduce((a, c) => a + c.small.plateCost, 0),
          dieCost: componentBreakdowns.reduce((a, c) => a + c.small.dieCost, 0),
          printCost: componentBreakdowns.reduce((a, c) => a + c.small.printCost, 0),
          postPressCost: componentBreakdowns.reduce((a, c) => a + c.small.finishingCost, 0) + totalAssemblyCost,
        },
        large: {
          machineKey: 'LARGE',
          machineName: primaryMachine.name || 'Máy In Offset 4 Màu Khổ 72x102',
          sheetFormat: `${primaryMachine.maxSheetWidth || 720} x ${primaryMachine.maxSheetLength || 1020} mm`,
          sheetWidth: primaryMachine.maxSheetWidth || 720,
          sheetLength: primaryMachine.maxSheetLength || 1020,
          totalCost: largeGrandTotal,
          unitPrice: largeUnitPrice,
          quotationTotal: largeQuoteTotal,
          marginAmount: largeMargin,
          isRecommended: isSingleMachine ? true : !isSmallBetter,
          ups: activeBreakdown.large.ups,
          cols: activeBreakdown.large.cols,
          rows: activeBreakdown.large.rows,
          netSheets: activeBreakdown.large.netSheets,
          grossSheets: activeBreakdown.large.grossSheets,
          paperCost: componentBreakdowns.reduce((a, c) => a + c.large.paperCost, 0),
          plateCost: componentBreakdowns.reduce((a, c) => a + c.large.plateCost, 0),
          dieCost: componentBreakdowns.reduce((a, c) => a + c.large.dieCost, 0),
          printCost: componentBreakdowns.reduce((a, c) => a + c.large.printCost, 0),
          postPressCost: componentBreakdowns.reduce((a, c) => a + c.large.finishingCost, 0) + totalAssemblyCost,
        },
      },
    };
  }, [debouncedComponents, debouncedOrderQty, activeComp.id, assemblyCostPerUnit, glueType, hasWindowPatching, packagingType, marginPercent, isSingleMachine, primaryMachine]);

  // ── 5. MULTI-TIER PRICING MATRIX ──
  const tierQuantities = [1000, 3000, 5000, 7000, 10000, 20000, 50000, 100000];
  const matrixData = useMemo(() => {
    return tierQuantities.map(qty => {
      const scale = qty / Math.max(1, debouncedOrderQty);
      const sTotal = (simulationData.plans.small.totalCost - simulationData.plans.small.plateCost - simulationData.plans.small.dieCost) * scale + simulationData.plans.small.plateCost + simulationData.plans.small.dieCost;
      const sPrice = Math.round((sTotal * (1 + marginPercent / 100)) / qty);

      const lTotal = (simulationData.plans.large.totalCost - simulationData.plans.large.plateCost - simulationData.plans.large.dieCost) * scale + simulationData.plans.large.plateCost + simulationData.plans.large.dieCost;
      const lPrice = Math.round((lTotal * (1 + marginPercent / 100)) / qty);

      const isSmall = sPrice <= lPrice;
      return {
        qty,
        smallPrice: sPrice,
        largePrice: lPrice,
        recPrice: isSmall ? sPrice : lPrice,
        isSmall,
      };
    });
  }, [simulationData, marginPercent, orderQty]);

  // ── 6. 1-CLICK QUOTATION MUTATION ──
  const convertMutation = useMutation({
    ...trpc.estimating.convertTradeoffToQuotation.mutationOptions(),
    onSuccess: (res) => {
      toast.success('Đã tạo báo giá thành công: ' + res.quotationCode);
      setIsQuoteModalOpen(false);
      queryClient.invalidateQueries({ queryKey: trpc.quotations.list.queryKey() });
      window.location.href = '/quotations';
    },
    onError: (err: any) => {
      toast.error('Lỗi khi tạo báo giá: ' + err.message);
    },
  });

  const handleOpenQuoteModal = (mKey: 'SMALL' | 'LARGE', qty: number) => {
    setTargetMachineKey(mKey);
    setTargetOrderQty(qty);
    if (!selectedCustomerId && customerList.length > 0) {
      setSelectedCustomerId(customerList[0].id || customerList[0].code);
    }
    setIsQuoteModalOpen(true);
  };

  const handleConfirmCreateQuote = () => {
    if (!selectedCustomerId) {
      toast.error('Vui lòng chọn khách hàng để tạo báo giá');
      return;
    }
    convertMutation.mutate({
      customerId: selectedCustomerId,
      productName: customProductName,
      orderQty: targetOrderQty,
      selectedMachineKey: targetMachineKey,
      tradeoffParams: {
        orderQty: targetOrderQty,
        flatWidth: activeComp.flatWidth,
        flatLength: activeComp.flatLength,
        paperType: activeComp.paperType,
        gsm: activeComp.gsm,
        paperPriceKg: activeComp.paperPriceKg,
        colors: activeComp.colorsFront,
        spotColors: activeComp.spotColorsFront,
        hasLamination: activeComp.laminationSides !== 'NONE',
        laminationType: activeComp.laminationType as any,
        hasFlute: activeComp.mountingType !== 'NONE',
        fluteType: activeComp.mountingType as any,
        glueType,
        marginPercent,
        overheadPercent,
        baseThreshold: 2000,
      },
      notes: `Đa thành phần (${components.length} chi tiết: ${components.map(c => c.name).join(', ')})`,
    });
  };

  const small = simulationData.plans.small;
  const large = simulationData.plans.large;
  const isSmallRec = simulationData.recommendedKey === 'SMALL';
  const activePlan = isSingleMachine ? large : (activeImpositionView === 'SMALL' ? small : large);

  // Active component imposition metrics
  const activeBd = simulationData.activeBreakdown;
  const activePlanMetrics = isSingleMachine ? activeBd.large : (activeImpositionView === 'SMALL' ? activeBd.small : activeBd.large);
  const sheetW = activePlan.sheetWidth;
  const sheetH = activePlan.sheetLength;
  const cols = activePlanMetrics.cols;
  const rows = activePlanMetrics.rows;
  const totalUps = activePlanMetrics.ups;
  const usableAreaPct = Math.min(96, Math.round(((totalUps * activeBd.effW * activeBd.effL) / (sheetW * sheetH)) * 100));

  const selectedPresetObj = (boxPresets as any[]).find(p => p.code === activeComp.boxPresetCode);

  return (
    <>
    <div className="space-y-2 max-h-[calc(100vh-100px)] overflow-y-auto pr-1">
      {/* ── 1. LEAN TOOLBAR & SẢN LƯỢNG ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-xs">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
            <Calculator className="h-3.5 w-3.5 text-indigo-600" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-900">Dự Toán Đa Thành Phần</span>
            <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[9px] py-0 px-1.5 font-medium">
              ✦ {components.length} Chi Tiết
            </Badge>
          </div>
        </div>

        {/* SẢN LƯỢNG NHANH */}
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[11px] text-slate-500 font-medium mr-1 hidden md:inline">Sản lượng (pcs):</span>
          {tierQuantities.map(qty => (
            <Button
              key={qty}
              size="sm"
              variant={orderQty === qty ? 'default' : 'outline'}
              className={`h-5 px-1.5 text-[10px] font-semibold rounded ${
                orderQty === qty
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-xs'
                  : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
              }`}
              onClick={() => setOrderQty(qty)}
            >
              {qty >= 1000 ? `${qty / 1000}k` : qty}
            </Button>
          ))}
        </div>
      </div>

      {/* ── 2. THANH TAB QUẢN LÝ ĐA THÀNH PHẦN (COMPONENT TABS) ── */}
      <div className="flex items-center justify-between bg-slate-100/90 p-1.5 rounded-lg border border-slate-200">
        <div className="flex items-center gap-1 overflow-x-auto">
          <span className="text-[10px] font-bold text-slate-600 mr-1 flex items-center gap-0.5">
            <Component className="h-3 w-3 text-indigo-600" />
            Chi Tiết:
          </span>
          {components.map((comp, idx) => (
            <div
              key={comp.id}
              className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold cursor-pointer border transition-all ${
                activeComponentId === comp.id
                  ? 'bg-white text-indigo-700 border-indigo-300 shadow-xs ring-1 ring-indigo-500/20'
                  : 'bg-slate-50/70 text-slate-600 border-transparent hover:bg-white'
              }`}
              onClick={() => setActiveComponentId(comp.id)}
            >
              <span>{idx + 1}. {comp.name}</span>
              <span className="text-[9px] text-slate-400 font-mono">({comp.paperType} {comp.gsm})</span>
              {components.length > 1 && (
                <button
                  type="button"
                  className="text-slate-400 hover:text-rose-600 ml-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteComponent(comp.id);
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>

        <Button
          size="sm"
          variant="outline"
          className="h-5 px-1.5 text-[9px] font-bold bg-white text-indigo-600 border-indigo-200 hover:bg-indigo-50 shrink-0 ml-2"
          onClick={handleAddComponent}
        >
          <Plus className="h-3 w-3 mr-0.5" />
          Thêm Chi Tiết
        </Button>
      </div>

      {/* ── 3. BỐ CỤC 3 PHÂN VÙNG DESKTOP SIÊU MẬT ĐỘ ── */}
      <div className="grid grid-cols-12 gap-2">
        
                <div className="col-span-12 lg:col-span-3 space-y-2">
          {/* 1. MẪU BAO BÌ & TÊN CHI TIẾT */}
          <Card className="border-slate-200 shadow-xs">
            <CardHeader className="bg-slate-50/90 py-1.5 px-2.5 border-b">
              <CardTitle className="text-[11px] font-bold flex items-center justify-between text-slate-800">
                <span className="flex items-center gap-1">
                  <Box className="h-3 w-3 text-indigo-600" />
                  Quy cách
                </span>
                <span className="text-[10px] font-mono font-bold text-indigo-700 bg-indigo-50 px-1 py-0.2 rounded border border-indigo-100">
                  {activeBd.effW} x {activeBd.effL} mm
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2 space-y-1.5 text-xs">
              <div>
                <Label className="text-[9px] text-slate-500">Tên chi tiết:</Label>
                <Input
                  value={activeComp.name}
                  onChange={e => updateActiveComponent({ name: e.target.value })}
                  className="h-6 text-[11px] font-semibold mt-0.5"
                />
              </div>

              <div>
                <Label className="text-[10px] text-slate-500 font-medium">Mẫu chuẩn:</Label>
                <Popover open={isPresetOpen} onOpenChange={setIsPresetOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={isPresetOpen}
                      className="h-6 w-full justify-between text-[11px] font-normal px-2 mt-0.5"
                    >
                      <span className="truncate">
                        {selectedPresetObj ? `[${selectedPresetObj.standard}] ${selectedPresetObj.name}` : 'Chọn mẫu...'}
                      </span>
                      <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[320px] p-0 shadow-lg" align="start">
                    <Command>
                      <CommandInput placeholder="Tìm kiếm mẫu ECMA / FEFCO..." className="h-7 text-xs" />
                      <CommandList className="max-h-56">
                        <CommandEmpty className="py-2 text-center text-xs text-slate-500">Không tìm thấy mẫu.</CommandEmpty>
                        <CommandGroup>
                          {(boxPresets as any[]).map(preset => (
                            <CommandItem
                              key={preset.code}
                              value={`${preset.code} ${preset.name} ${preset.standard}`}
                              onSelect={() => handleSelectBoxPreset(preset.code)}
                              className="text-xs py-1.5 cursor-pointer"
                            >
                              <Check className={`mr-1.5 h-3.5 w-3.5 ${activeComp.boxPresetCode === preset.code ? 'opacity-100 text-indigo-600' : 'opacity-0'}`} />
                              <div className="flex flex-col">
                                <span className="font-semibold text-slate-800">[{preset.standard}] {preset.name}</span>
                                <span className="text-[10px] text-slate-400 font-mono">Mã: {preset.code}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="grid grid-cols-2 gap-1">
                <div>
                  <Label className="text-[9px] text-slate-500">Trải Ngang (W)</Label>
                  <Input
                    type="number"
                    value={activeComp.flatWidth}
                    onChange={e => updateActiveComponent({ flatWidth: Number(e.target.value) })}
                    className="h-6 text-[11px] font-semibold mt-0.5"
                  />
                </div>
                <div>
                  <Label className="text-[9px] text-slate-500">Trải Dọc (L)</Label>
                  <Input
                    type="number"
                    value={activeComp.flatLength}
                    onChange={e => updateActiveComponent({ flatLength: Number(e.target.value) })}
                    className="h-6 text-[11px] font-semibold mt-0.5"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 2. CHIỀU SỚ GIẤY & KIỂU IN (WORK & TURN / AB) */}
          <Card className="border-indigo-100 shadow-xs bg-indigo-50/20">
            <CardHeader className="bg-indigo-50/70 py-1.5 px-2.5 border-b">
              <CardTitle className="text-[11px] font-bold flex items-center justify-between text-indigo-950">
                <span className="flex items-center gap-1">
                  <Compass className="h-3 w-3 text-indigo-600" />
                  Kiểu In
                </span>
                {activeComp.printScheme === 'WORK_AND_TURN' && (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 text-[8px] py-0 px-1 font-bold">
                    ⚡ Tiết kiệm 50% kẽm
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2 space-y-1.5 text-xs">
              <div>
                <Label className="text-[9px] text-slate-500">Kiểu In Bản Offset:</Label>
                <Select
                  value={activeComp.printScheme}
                  onValueChange={(v: any) => updateActiveComponent({ printScheme: v })}
                >
                  <SelectTrigger className="h-6 mt-0.5 text-[10px] font-semibold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SINGLE_SIDED">1 Mặt (4/0, 1/0)</SelectItem>
                    <SelectItem value="WORK_AND_TURN">In Tự Trở Lật Hông (Work & Turn)</SelectItem>
                    <SelectItem value="WORK_AND_TUMBLE">In Tự Trở Lật Đầu Đuôi (Tumble)</SelectItem>
                    <SelectItem value="SHEETWISE_AB">In Trở Khác Bản (AB - 2 bộ kẽm)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[9px] text-slate-500">Chiều sớ giấy:</Label>
                <div className="grid grid-cols-3 gap-1 mt-0.5">
                  <Button
                    type="button"
                    size="sm"
                    variant={activeComp.grainDirection === 'MD' ? 'default' : 'outline'}
                    className={`h-5 text-[9px] font-bold px-1 ${activeComp.grainDirection === 'MD' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-700'}`}
                    onClick={() => updateActiveComponent({ grainDirection: 'MD' })}
                  >
                    MD (Dọc)
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={activeComp.grainDirection === 'CD' ? 'default' : 'outline'}
                    className={`h-5 text-[9px] font-bold px-1 ${activeComp.grainDirection === 'CD' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-700'}`}
                    onClick={() => updateActiveComponent({ grainDirection: 'CD' })}
                  >
                    CD (Ngang)
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={activeComp.grainDirection === 'AUTO' ? 'default' : 'outline'}
                    className={`h-5 text-[9px] font-bold px-1 ${activeComp.grainDirection === 'AUTO' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-700'}`}
                    onClick={() => updateActiveComponent({ grainDirection: 'AUTO' })}
                  >
                    Auto
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 3. GIẤY MỘC & MÀU IN */}
          <Card className="border-slate-200 shadow-xs">
            <CardHeader className="bg-slate-50/90 py-1.5 px-2.5 border-b">
              <CardTitle className="text-[11px] font-bold flex items-center gap-1 text-slate-800">
                <Printer className="h-3 w-3 text-indigo-600" />
                3. Giấy & Màu In
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2 space-y-1.5 text-xs">
              <div>
                <Label className="text-[9px] text-slate-500">Kho giấy WMS:</Label>
                <Popover open={isMaterialOpen} onOpenChange={setIsMaterialOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={isMaterialOpen}
                      className="h-6 w-full justify-between text-[10px] font-normal px-2 mt-0.5"
                    >
                      <span className="truncate">
                        {activeComp.paperType} {activeComp.gsm}gsm - {activeComp.paperPriceKg.toLocaleString()}đ/kg
                      </span>
                      <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[340px] p-0 shadow-lg" align="start">
                    <Command>
                      <CommandInput placeholder="Tìm kiếm giấy Ivory, Couche, Fort, GSM..." className="h-7 text-xs" />
                      <CommandList className="max-h-56">
                        <CommandEmpty className="py-2 text-center text-xs text-slate-500">Không tìm thấy vật tư.</CommandEmpty>
                        <CommandGroup>
                          {materialList.map((mat: any, idx: number) => (
                            <CommandItem
                              key={String(mat.id || mat.code || idx)}
                              value={`${mat.code} ${mat.name} ${mat.gsm}`}
                              onSelect={() => {
                                let pType = 'Ivory';
                                if (mat.name?.toLowerCase().includes('couche')) pType = 'Couche';
                                else if (mat.name?.toLowerCase().includes('duplex')) pType = 'Duplex';
                                else if (mat.name?.toLowerCase().includes('kraft')) pType = 'Kraft';
                                else if (mat.name?.toLowerCase().includes('bristol')) pType = 'Bristol';
                                else if (mat.name?.toLowerCase().includes('fort')) pType = 'Fort';

                                updateActiveComponent({
                                  paperType: pType,
                                  gsm: mat.gsm ? Number(mat.gsm) : activeComp.gsm,
                                  paperPriceKg: mat.unitPrice && Number(mat.unitPrice) > 0 ? Number(mat.unitPrice) : activeComp.paperPriceKg,
                                });
                                setIsMaterialOpen(false);
                                toast.success('Đã chọn: ' + mat.name);
                              }}
                              className="text-xs py-1.5 cursor-pointer"
                            >
                              <div className="flex flex-col">
                                <span className="font-semibold text-slate-800">[{mat.code || 'WMS'}] {mat.name}</span>
                                <span className="text-[10px] text-slate-400 font-mono">
                                  {mat.gsm ? `${mat.gsm} gsm • ` : ''}Giá: {Number(mat.unitPrice || activeComp.paperPriceKg).toLocaleString()} đ/kg
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="grid grid-cols-2 gap-1">
                <div>
                  <Label className="text-[9px] text-slate-500">Loại giấy</Label>
                  <Select value={activeComp.paperType} onValueChange={v => updateActiveComponent({ paperType: v })}>
                    <SelectTrigger className="h-6 mt-0.5 text-[10px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Ivory">Ivory</SelectItem>
                      <SelectItem value="Couche">Couche</SelectItem>
                      <SelectItem value="Duplex">Duplex</SelectItem>
                      <SelectItem value="Kraft">Kraft</SelectItem>
                      <SelectItem value="Bristol">Bristol</SelectItem>
                      <SelectItem value="Fort">Fort</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[9px] text-slate-500">Định lượng (gsm)</Label>
                  <Input
                    type="number"
                    value={activeComp.gsm}
                    onChange={e => updateActiveComponent({ gsm: Number(e.target.value) })}
                    className="h-6 text-[10px] font-semibold mt-0.5"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-1">
                <div>
                  <Label className="text-[9px] text-slate-500">Giá giấy/kg</Label>
                  <Input
                    type="number"
                    value={activeComp.paperPriceKg}
                    onChange={e => updateActiveComponent({ paperPriceKg: Number(e.target.value) })}
                    className="h-6 text-[10px] font-semibold mt-0.5"
                  />
                </div>
                <div>
                  <Label className="text-[9px] text-slate-500">Màu CMYK</Label>
                  <Input
                    type="number"
                    value={activeComp.colorsFront}
                    onChange={e => updateActiveComponent({ colorsFront: Number(e.target.value) })}
                    className="h-6 text-[10px] font-semibold mt-0.5"
                  />
                </div>
                <div>
                  <Label className="text-[9px] text-slate-500">Màu pha</Label>
                  <Input
                    type="number"
                    value={activeComp.spotColorsFront}
                    onChange={e => updateActiveComponent({ spotColorsFront: Number(e.target.value) })}
                    className="h-6 text-[10px] font-semibold mt-0.5"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

                <div className="col-span-12 lg:col-span-5 space-y-2">
          <Card className="border-indigo-200 shadow-xs h-full flex flex-col">
            <CardHeader className="bg-indigo-50/80 py-1.5 px-3 border-b flex flex-row items-center justify-between">
              <CardTitle className="text-xs font-bold flex items-center gap-1.5 text-indigo-950">
                <Maximize2 className="h-3.5 w-3.5 text-indigo-600" />
                {activeComp.name}
              </CardTitle>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-6 px-1.5 text-[10px] bg-white border-indigo-200"
                  onClick={() => updateActiveComponent({ isRotated90: !activeComp.isRotated90 })}
                >
                  <RotateCw className="h-3 w-3 mr-1" />
                  Xoay 90°
                </Button>
                {isSingleMachine ? (
                  <Badge className="bg-indigo-600 text-white text-[10px] px-2 py-0.5 font-bold shadow-xs">
                    Khổ 72x102 ({activeBd.large.ups} con)
                  </Badge>
                ) : (
                  <div className="flex bg-white p-0.5 rounded border border-indigo-200">
                    <button
                      type="button"
                      className={`px-2 py-0.5 text-[10px] font-bold rounded transition-colors ${activeImpositionView === 'SMALL' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
                      onClick={() => setActiveImpositionView('SMALL')}
                    >
                      Khổ Nhỏ ({activeBd.small.ups})
                    </button>
                    <button
                      type="button"
                      className={`px-2 py-0.5 text-[10px] font-bold rounded transition-colors ${activeImpositionView === 'LARGE' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
                      onClick={() => setActiveImpositionView('LARGE')}
                    >
                      Khổ Lớn ({activeBd.large.ups})
                    </button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-3 flex-1 flex flex-col">
              <div className="relative bg-slate-900 rounded-lg p-3 text-white flex flex-col items-center flex-1 min-h-[250px] border border-slate-800">
                <div className="w-full flex justify-between items-center text-[10px] text-slate-300 font-mono mb-2 px-1">
                  <span className="text-amber-400 font-semibold flex items-center"><ChevronUp className="h-3 w-3 inline mr-0.5"/> Nhíp: 12mm</span>
                  <span className="text-cyan-300 font-bold px-2 py-0.5 bg-cyan-900/30 rounded">
                    {activeComp.printScheme === 'WORK_AND_TURN' ? '⚡ Tự Trở' : activeComp.printScheme === 'SHEETWISE_AB' ? 'Trở AB' : '1 Mặt'}
                  </span>
                  <span>Hữu ích: <strong className="text-emerald-400 text-xs">{usableAreaPct}%</strong></span>
                </div>

                {(() => {
                  const svgW = 340;
                  const svgH = Math.max(120, Math.round((svgW * sheetH) / sheetW));
                  const strokeW = svgH > 300 ? 1.5 : 1;
                  const grainY = svgH - 10;
                  return (
                    <svg
                      viewBox={`0 0 ${svgW} ${svgH}`}
                      className="w-full max-h-[45vh] lg:max-h-[30vh] rounded border border-dashed border-indigo-400/50 bg-slate-800/80 shadow-inner mt-1"
                    >
                      {/* Giấy nền */}
                      <rect x="2" y="2" width={svgW - 4} height={svgH - 4} fill="rgba(30, 41, 59, 0.6)" stroke="#6366f1" strokeWidth={strokeW} rx="3" />
                      {/* Vùng nhíp */}
                      <rect x="2" y="2" width={svgW - 4} height={svgH * (12 / sheetH)} fill="#f59e0b" opacity="0.45" />

                      {/* Mũi tên chiều sớ giấy */}
                      <line x1="20" y1={grainY} x2="100" y2={grainY} stroke="rgba(6, 182, 212, 0.5)" strokeWidth="1.5" strokeDasharray="3,3" />
                      <polygon points={`102,${grainY} 96,${grainY - 3} 96,${grainY + 3}`} fill="rgba(6, 182, 212, 0.7)" />
                      <text x="110" y={grainY + 3} fill="rgba(6, 182, 212, 0.7)" fontSize="7" fontStyle="italic">Chiều sớ: {activeComp.grainDirection}</text>

                      {Array.from({ length: cols }).map((_, cIdx) => {
                        const colW = (svgW - 16) / cols;
                        return Array.from({ length: rows }).map((_, rIdx) => {
                          const rowH = (svgH - 24) / rows;
                          const x = 8 + cIdx * colW + 2;
                          const y = 16 + rIdx * rowH + 2;
                          const w = colW - 4;
                          const h = rowH - 4;
                          return (
                            <g key={`${cIdx}-${rIdx}`}>
                              <rect
                                x={x}
                                y={y}
                                width={w}
                                height={h}
                                fill="#10b981"
                                opacity="0.25"
                                stroke="#34d399"
                                strokeWidth="0.8"
                                rx="2"
                              />
                              <text
                                x={x + w / 2}
                                y={y + h / 2 + (svgH > 200 ? 4 : 2)}
                                fill="#34d399"
                                fontSize={svgH > 200 ? "10" : "7"}
                                fontWeight="bold"
                                textAnchor="middle"
                              >
                                {cIdx * rows + rIdx + 1}
                              </text>
                            </g>
                          );
                        });
                      })}
                    </svg>
                  );
                })()}

                <div className="mt-1 flex items-center justify-between text-[9px] text-slate-500">
                  <span>Bình: <strong className="text-indigo-400">{totalUps} con/tờ</strong></span>
                  <span>Tờ mộc: <strong className="text-emerald-400">{activePlanMetrics.grossSheets.toLocaleString()} tờ</strong></span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* BẢNG TỔNG HỢP CHI PHÍ BÓC TÁCH THEO TỪNG THÀNH PHẦN */}
          <Card className="border-slate-200 shadow-xs">
            <CardHeader className="bg-slate-50/90 py-1.5 px-2.5 border-b flex flex-row items-center justify-between">
              <CardTitle className="text-[11px] font-bold text-slate-800 flex items-center gap-1">
                <FolderKanban className="h-3.5 w-3.5 text-indigo-600" />
                Bảng Phân Bổ Chi Phí Đa Thành Phần
              </CardTitle>
              <Badge variant="outline" className="text-[8px] bg-white font-bold text-indigo-600">
                SL: {orderQty.toLocaleString()} pcs
              </Badge>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="text-xs">
                <TableHeader>
                  <TableRow className="bg-slate-50/60 hover:bg-slate-50/60 text-[9px]">
                    <TableHead className="py-0.5 px-2 font-bold">Chi Tiết</TableHead>
                    <TableHead className="py-0.5 px-1 text-right font-bold">Tiền Giấy</TableHead>
                    <TableHead className="py-0.5 px-1 text-right font-bold">Kẽm CTP</TableHead>
                    <TableHead className="py-0.5 px-1 text-right font-bold">In & GC</TableHead>
                    <TableHead className="py-0.5 px-2 text-right font-bold">Thành Tiền</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {simulationData.componentBreakdowns.map((cb, idx) => {
                    const metrics = activeImpositionView === 'SMALL' ? cb.small : cb.large;
                    return (
                      <TableRow key={cb.comp.id} className="hover:bg-slate-50/50 text-[10px]">
                        <TableCell className="py-0.5 px-2 font-medium text-slate-800">
                          {idx + 1}. {cb.comp.name}
                        </TableCell>
                        <TableCell className="py-0.5 px-1 text-right font-mono text-slate-600">
                          {metrics.paperCost.toLocaleString()} đ
                        </TableCell>
                        <TableCell className="py-0.5 px-1 text-right font-mono text-slate-600">
                          {metrics.plateCost.toLocaleString()} đ
                        </TableCell>
                        <TableCell className="py-0.5 px-1 text-right font-mono text-slate-600">
                          {(metrics.printCost + metrics.finishingCost).toLocaleString()} đ
                        </TableCell>
                        <TableCell className="py-0.5 px-2 text-right font-mono font-bold text-indigo-700">
                          {metrics.totalCost.toLocaleString()} đ
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className="bg-indigo-50/40 font-semibold text-[10px]">
                    <TableCell className="py-0.5 px-2 text-indigo-900" colSpan={4}>
                      ✦ Lắp Ráp Hoàn Thiện & Đóng Gói
                    </TableCell>
                    <TableCell className="py-0.5 px-2 text-right font-mono text-indigo-900 font-bold">
                      {simulationData.totalAssemblyCost.toLocaleString()} đ
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* BANNER KHUYẾN NGHỊ & NÚT TẠO BÁO GIÁ */}
          <div className={`p-2 rounded-lg border flex items-center justify-between gap-2 shadow-xs ${
            isSingleMachine || !isSmallRec
              ? 'bg-blue-50/90 border-blue-300 text-blue-950'
              : 'bg-emerald-50/90 border-emerald-300 text-emerald-950'
          }`}>
            <div className="flex items-center gap-1.5 min-w-0">
              <div className={`p-1 rounded shrink-0 ${isSingleMachine || !isSmallRec ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white'}`}>
                <Sparkles className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0">
                <h5 className="font-extrabold text-[11px] truncate">
                  {isSingleMachine
                    ? '🏆 THIẾT BỊ XƯỞNG: MÁY IN OFFSET KHỔ 72x102 (DUY NHẤT)'
                    : (isSmallRec ? '🏆 TỐI ƯU: KHỔ NHỎ (54x79)' : '🏆 TỐI ƯU: KHỔ LỚN (72x102)')}
                </h5>
                <p className="text-[9px] opacity-90 truncate">
                  {simulationData.summaryRecommendation}
                </p>
              </div>
            </div>

            <Button
              size="sm"
              className={`h-6 px-2 text-[11px] font-bold shrink-0 shadow-xs ${
                isSingleMachine || !isSmallRec ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
              onClick={() => handleOpenQuoteModal(isSingleMachine ? 'LARGE' : (isSmallRec ? 'SMALL' : 'LARGE'), orderQty)}
            >
              <FileText className="h-3 w-3 mr-1" />
              Tạo Báo Giá
            </Button>
          </div>

          {/* THẺ ĐỐI CHIẾU HOẶC HIỂN THỊ MÁY IN DUY NHẤT */}
          {isSingleMachine ? (
            <div className="p-2.5 rounded-lg border text-xs relative border-blue-500 bg-blue-50/20 ring-1 ring-blue-500/30">
              <span className="absolute -top-1.5 right-2 bg-blue-600 text-white px-2 py-0.5 rounded text-[8px] font-bold">
                ✓ THIẾT BỊ SẢN XUẤT XƯỞNG SỞ HỮU
              </span>
              <div className="font-bold text-slate-900 text-xs flex items-center gap-1.5">
                <Printer className="h-3.5 w-3.5 text-blue-600" />
                {primaryMachine.name || 'Máy In Offset 4 Màu Khổ 72x102'} ({primaryMachine.maxSheetWidth || 720} x {primaryMachine.maxSheetLength || 1020} mm)
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-sm font-extrabold text-blue-700 font-mono">
                  {large.unitPrice.toLocaleString()} đ<span className="text-[9px] font-normal text-slate-500">/bộ</span>
                </span>
                <span className="text-[11px] text-slate-600">
                  Tổng dự toán: <strong className="text-slate-800">{large.quotationTotal.toLocaleString()} đ</strong>
                </span>
              </div>
              <div className="text-[9px] text-slate-500 mt-1 border-t pt-1 flex items-center justify-between">
                <span>Tiền giấy: <strong className="text-slate-700">{large.paperCost.toLocaleString()} đ</strong></span>
                <span>Kẽm: <strong className="text-slate-700">{large.plateCost.toLocaleString()} đ</strong></span>
                <span>In: <strong className="text-slate-700">{large.printCost.toLocaleString()} đ</strong></span>
                <span>Gia công: <strong className="text-slate-700">{large.postPressCost.toLocaleString()} đ</strong></span>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-1.5">
              <div className={`p-2 rounded-lg border text-xs relative ${
                small.isRecommended
                  ? 'border-emerald-500 bg-emerald-50/20 ring-1 ring-emerald-500/30'
                  : 'border-slate-200 bg-white opacity-85'
              }`}>
                {small.isRecommended && (
                  <span className="absolute -top-1.5 right-1.5 bg-emerald-600 text-white px-1 py-0 rounded text-[8px] font-bold">
                    ✓ RẺ NHẤT
                  </span>
                )}
                <div className="font-bold text-slate-900 text-[11px] truncate">Khổ Nhỏ (54x79)</div>
                <div className="text-xs font-extrabold text-emerald-700 font-mono">
                  {small.unitPrice.toLocaleString()} đ<span className="text-[9px] font-normal text-slate-500">/bộ</span>
                </div>
                <div className="text-[9px] text-slate-500 mt-0.5 border-t pt-0.5">
                  Tổng: <strong className="text-slate-800">{small.quotationTotal.toLocaleString()} đ</strong>
                </div>
              </div>

              <div className={`p-2 rounded-lg border text-xs relative ${
                large.isRecommended
                  ? 'border-blue-500 bg-blue-50/20 ring-1 ring-blue-500/30'
                  : 'border-slate-200 bg-white opacity-85'
              }`}>
                {large.isRecommended && (
                  <span className="absolute -top-1.5 right-1.5 bg-blue-600 text-white px-1 py-0 rounded text-[8px] font-bold">
                    ✓ RẺ NHẤT
                  </span>
                )}
                <div className="font-bold text-slate-900 text-[11px] truncate">Khổ Lớn (72x102)</div>
                <div className="text-xs font-extrabold text-blue-700 font-mono">
                  {large.unitPrice.toLocaleString()} đ<span className="text-[9px] font-normal text-slate-500">/bộ</span>
                </div>
                <div className="text-[9px] text-slate-500 mt-0.5 border-t pt-0.5">
                  Tổng: <strong className="text-slate-800">{large.quotationTotal.toLocaleString()} đ</strong>
                </div>
              </div>
            </div>
          )}

          {/* BẢNG GIÁ BẬC THANG */}
          <Card className="border-slate-200 shadow-xs">
            <CardHeader className="bg-slate-50/90 py-1.5 px-2.5 border-b flex flex-row items-center justify-between">
              <CardTitle className="text-[11px] font-bold text-slate-800">
                Bảng Giá Bậc Thang Trọn Bộ ({components.length} chi tiết)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="text-xs">
                <TableHeader>
                  <TableRow className="bg-slate-50/60 hover:bg-slate-50/60 text-[9px]">
                    <TableHead className="py-0.5 px-2 font-bold">SL (pcs)</TableHead>
                    {!isSingleMachine && <TableHead className="py-0.5 px-1.5 text-right font-bold">Khổ Nhỏ</TableHead>}
                    {!isSingleMachine && <TableHead className="py-0.5 px-1.5 text-right font-bold">Khổ Lớn</TableHead>}
                    <TableHead className="py-0.5 px-2 text-right font-bold">
                      {isSingleMachine ? 'Đơn Giá Dự Toán (Khổ 72x102)' : 'Đơn Giá Tối Ưu'}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {matrixData.map((tier) => (
                    <TableRow
                      key={tier.qty}
                      className={`hover:bg-indigo-50/40 cursor-pointer text-[10px] ${orderQty === tier.qty ? 'bg-indigo-50/60 font-bold' : ''}`}
                      onClick={() => setOrderQty(tier.qty)}
                    >
                      <TableCell className="py-0.5 px-2 font-mono">{tier.qty.toLocaleString()}</TableCell>
                      {!isSingleMachine && (
                        <TableCell className={`py-0.5 px-1.5 text-right font-mono ${tier.isSmall ? 'text-emerald-700 font-bold' : 'text-slate-500'}`}>
                          {tier.smallPrice.toLocaleString()} đ
                        </TableCell>
                      )}
                      {!isSingleMachine && (
                        <TableCell className={`py-0.5 px-1.5 text-right font-mono ${!tier.isSmall ? 'text-blue-700 font-bold' : 'text-slate-500'}`}>
                          {tier.largePrice.toLocaleString()} đ
                        </TableCell>
                      )}
                      <TableCell className="py-0.5 px-2 text-right font-mono font-bold text-slate-900">
                        {(isSingleMachine ? tier.largePrice : tier.recPrice).toLocaleString()} đ
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

                <div className="col-span-12 lg:col-span-4 space-y-2">
          {/* THANH CÔNG CỤ QUY TRÌNH MẪU */}
          <div className="bg-slate-100/90 p-1.5 rounded-lg border border-slate-200 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-slate-700 flex items-center gap-1">
                <Zap className="h-3 w-3 text-amber-500" />
                Quy Trình Mẫu:
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-5 px-1.5 text-[9px] font-bold bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                onClick={() => setIsProcessMatrixOpen(true)}
              >
                <ListChecks className="h-3 w-3 mr-0.5" />
                Bảng Quy Trình
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-1">
              <Button
                size="sm"
                variant="outline"
                className="h-5 px-1 text-[9px] font-semibold bg-white hover:bg-slate-50 justify-start truncate"
                onClick={() => applyWorkflowPreset('COSMETIC')}
              >
                💊 Hộp Mỹ Phẩm
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-5 px-1 text-[9px] font-semibold bg-white hover:bg-slate-50 justify-start truncate"
                onClick={() => applyWorkflowPreset('LUXURY')}
              >
                🎁 Hộp Quà Luxury
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-5 px-1 text-[9px] font-semibold bg-white hover:bg-slate-50 justify-start truncate"
                onClick={() => applyWorkflowPreset('CORRUGATED')}
              >
                📦 Thùng Sóng Bồi
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-5 px-1 text-[9px] font-semibold bg-white hover:bg-slate-50 justify-start truncate"
                onClick={() => applyWorkflowPreset('WINDOW')}
              >
                🪟 Hộp Cửa Sổ Suppo
              </Button>
            </div>
          </div>

          {/* 1. MÀNG & SÓNG CỦA CHI TIẾT */}
          <Card className="border-slate-200 shadow-xs">
            <CardHeader className="bg-slate-50/90 py-1.5 px-2.5 border-b">
              <CardTitle className="text-[11px] font-bold flex items-center gap-1 text-slate-800">
                <Layers className="h-3 w-3 text-indigo-600" />
                1. Màng & Sóng: [{activeComp.name}]
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2 space-y-1.5 text-xs">
              <div className="grid grid-cols-2 gap-1">
                <div>
                  <Label className="text-[9px] text-slate-500">Mặt cán</Label>
                  <Select
                    value={activeComp.laminationSides}
                    onValueChange={(v: any) => updateActiveComponent({ laminationSides: v })}
                  >
                    <SelectTrigger className="h-6 mt-0.5 text-[10px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">Không cán</SelectItem>
                      <SelectItem value="ONE_SIDE">Cán 1 Mặt (1F)</SelectItem>
                      <SelectItem value="TWO_SIDES">Cán 2 Mặt (2F)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[9px] text-slate-500">Loại màng</Label>
                  <Select
                    value={activeComp.laminationType}
                    onValueChange={(v: any) => updateActiveComponent({ laminationType: v })}
                    disabled={activeComp.laminationSides === 'NONE'}
                  >
                    <SelectTrigger className="h-6 mt-0.5 text-[10px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BOPP_MATTE">Màng Mờ</SelectItem>
                      <SelectItem value="BOPP_GLOSS">Màng Bóng</SelectItem>
                      <SelectItem value="SOFT_TOUCH">Màng Nhung</SelectItem>
                      <SelectItem value="THERMAL">Màng Nhiệt</SelectItem>
                      <SelectItem value="METALLIZED">Metallized</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-[9px] text-slate-500">Bồi sóng / Bìa cứng</Label>
                <Select
                  value={activeComp.mountingType}
                  onValueChange={(v: any) => updateActiveComponent({ mountingType: v })}
                >
                  <SelectTrigger className="h-6 mt-0.5 text-[10px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">Không bồi sóng</SelectItem>
                    <SelectItem value="FLUTE_E">Sóng E (1.5mm - Hộp thuốc)</SelectItem>
                    <SelectItem value="FLUTE_B">Sóng B (3.0mm - Hộp giày)</SelectItem>
                    <SelectItem value="FLUTE_F">Sóng F (0.9mm - Mỏng)</SelectItem>
                    <SelectItem value="DUPLEX_MOUNT">Bồi Đôi Giấy (Duplex-Ivory)</SelectItem>
                    <SelectItem value="RIGID_BOARD">Bồi Bìa Cứng (Carton Lạnh 2mm)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* 2. ÉP KIM & HIỆU ỨNG */}
          <Card className="border-slate-200 shadow-xs">
            <CardHeader className="bg-slate-50/90 py-1.5 px-2.5 border-b">
              <CardTitle className="text-[11px] font-bold flex items-center justify-between text-slate-800">
                <span className="flex items-center gap-1">
                  <Sparkles className="h-3 w-3 text-amber-500" />
                  2. Ép Kim & Hiệu Ứng
                </span>
                <span className="text-[9px] text-slate-500">[{activeComp.name}]</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2 space-y-1.5 text-xs">
              <div className="grid grid-cols-2 gap-1">
                <label className="flex items-center gap-1 text-[10px] cursor-pointer">
                  <Checkbox
                    checked={activeComp.foilGold}
                    onCheckedChange={(v: any) => updateActiveComponent({ foilGold: !!v })}
                  />
                  <span>Nhũ Vàng Gold</span>
                </label>
                <label className="flex items-center gap-1 text-[10px] cursor-pointer">
                  <Checkbox
                    checked={activeComp.foilSilver}
                    onCheckedChange={(v: any) => updateActiveComponent({ foilSilver: !!v })}
                  />
                  <span>Nhũ Bạc Silver</span>
                </label>
                <label className="flex items-center gap-1 text-[10px] cursor-pointer">
                  <Checkbox
                    checked={activeComp.hasSpotUv}
                    onCheckedChange={(v: any) => updateActiveComponent({ hasSpotUv: !!v })}
                  />
                  <span>Spot UV</span>
                </label>
                <label className="flex items-center gap-1 text-[10px] cursor-pointer">
                  <Checkbox
                    checked={activeComp.hasEmbossing}
                    onCheckedChange={(v: any) => updateActiveComponent({ hasEmbossing: !!v })}
                  />
                  <span>Dập Nổi 3D</span>
                </label>
              </div>

              {(activeComp.foilGold || activeComp.foilSilver) && (
                <div className="pt-1 border-t flex items-center justify-between">
                  <Label className="text-[9px] text-slate-500">Diện tích ép (cm²):</Label>
                  <Input
                    type="number"
                    value={activeComp.foilAreaCm2}
                    onChange={e => updateActiveComponent({ foilAreaCm2: Number(e.target.value) })}
                    className="h-5 w-16 text-[10px] font-mono text-right"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* 3. LẮP RÁP & ĐÓNG GÓI CHUNG */}
          <Card className="border-slate-200 shadow-xs">
            <CardHeader className="bg-slate-50/90 py-1.5 px-2.5 border-b">
              <CardTitle className="text-[11px] font-bold flex items-center gap-1 text-slate-800">
                <Wand2 className="h-3 w-3 text-indigo-600" />
                3. Lắp Ráp & Đóng Gói
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2 space-y-1.5 text-xs">
              <div className="grid grid-cols-2 gap-1">
                <div>
                  <Label className="text-[9px] text-slate-500">Lắp ráp (đ/bộ)</Label>
                  <Input
                    type="number"
                    value={assemblyCostPerUnit}
                    onChange={e => setAssemblyCostPerUnit(Number(e.target.value))}
                    className="h-6 text-[10px] font-semibold mt-0.5"
                  />
                </div>
                <div>
                  <Label className="text-[9px] text-slate-500">Đóng gói</Label>
                  <Select value={packagingType} onValueChange={(v: any) => setPackagingType(v)}>
                    <SelectTrigger className="h-6 mt-0.5 text-[10px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CARTON_3L">Thùng 3 Lớp</SelectItem>
                      <SelectItem value="CARTON_5L">Thùng 5 Lớp</SelectItem>
                      <SelectItem value="PE_BAG">Túi PE</SelectItem>
                      <SelectItem value="PALLET">Quấn Pallet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="pt-1 border-t flex items-center justify-between">
                <label className="flex items-center gap-1 text-[9px] cursor-pointer text-indigo-700 font-bold">
                  <Checkbox checked={hasWindowPatching} onCheckedChange={(v: any) => setHasWindowPatching(!!v)} />
                  <span>Suppo PET</span>
                </label>
                <div className="flex items-center gap-1">
                  <span className="text-[9px] text-slate-500">Margin:</span>
                  <Input
                    type="number"
                    value={marginPercent}
                    onChange={e => setMarginPercent(Number(e.target.value))}
                    className="h-5 w-12 text-[10px] font-semibold text-right"
                  />
                  <span className="text-[9px] text-slate-500">%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── MODAL BẢNG QUY TRÌNH SẢN XUẤT ── */}
      <Dialog open={isProcessMatrixOpen} onOpenChange={setIsProcessMatrixOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-sm font-bold flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-indigo-600" />
              Bảng Tích Chọn Quy Trình Cho [{activeComp.name}]
            </DialogTitle>
            <DialogDescription className="text-xs">
              Tích chọn công đoạn sản xuất để áp dụng ngay vào chi tiết đang chọn.
            </DialogDescription>
          </DialogHeader>

          <div className="py-1 space-y-1.5 max-h-80 overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="p-2 rounded border bg-slate-50/60 space-y-1.5">
                <span className="font-bold text-[11px] text-slate-800 block border-b pb-0.5">Tiền In & In Ấn</span>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox checked={true} disabled />
                  <span className="text-[11px]">1. Xén Giấy Mộc</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox checked={true} disabled />
                  <span className="text-[11px]">2. Xuất Kẽm CTP</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox checked={true} disabled />
                  <span className="text-[11px]">3. In Offset ({activeComp.colorsFront}M CMYK)</span>
                </label>
              </div>

              <div className="p-2 rounded border bg-slate-50/60 space-y-1.5">
                <span className="font-bold text-[11px] text-slate-800 block border-b pb-0.5">Màng & Bồi</span>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox
                    checked={activeComp.laminationSides !== 'NONE'}
                    onCheckedChange={(v: any) => updateActiveComponent({ laminationSides: v ? 'ONE_SIDE' : 'NONE' })}
                  />
                  <span className="text-[11px]">4. Cán Màng BOPP</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox
                    checked={activeComp.mountingType === 'RIGID_BOARD'}
                    onCheckedChange={(v: any) => updateActiveComponent({ mountingType: v ? 'RIGID_BOARD' : 'NONE' })}
                  />
                  <span className="text-[11px]">5. Bồi Carton Lạnh 2mm</span>
                </label>
              </div>

              <div className="p-2 rounded border bg-slate-50/60 space-y-1.5">
                <span className="font-bold text-[11px] text-slate-800 block border-b pb-0.5">Ép Kim & Hiệu Ứng</span>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox
                    checked={activeComp.foilGold}
                    onCheckedChange={(v: any) => updateActiveComponent({ foilGold: !!v })}
                  />
                  <span className="text-[11px]">6. Ép Kim Nhũ Vàng</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox
                    checked={activeComp.hasSpotUv}
                    onCheckedChange={(v: any) => updateActiveComponent({ hasSpotUv: !!v })}
                  />
                  <span className="text-[11px]">7. Phủ Spot UV</span>
                </label>
              </div>

              <div className="p-2 rounded border bg-slate-50/60 space-y-1.5">
                <span className="font-bold text-[11px] text-slate-800 block border-b pb-0.5">Bế & Lắp Ráp</span>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox checked={true} disabled />
                  <span className="text-[11px]">8. Bế Khuôn Tự Động</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox
                    checked={hasWindowPatching}
                    onCheckedChange={(v: any) => setHasWindowPatching(!!v)}
                  />
                  <span className="text-[11px]">9. Dán Suppo PET</span>
                </label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold" onClick={() => setIsProcessMatrixOpen(false)}>
              Áp Dụng Cho Chi Tiết
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── DIALOG 1-CLICK TẠO BÁO GIÁ ── */}
      <Dialog open={isQuoteModalOpen} onOpenChange={setIsQuoteModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <FileText className="h-5 w-5 text-indigo-600" />
              1-Click Tạo Báo Giá Đa Thành Phần
            </DialogTitle>
            <DialogDescription className="text-xs">
              Hệ thống tự động kế thừa toàn bộ {components.length} chi tiết, tiền kẽm CTP, tiền giấy và chi phí lắp ráp sang Báo Giá mới.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2 text-xs">
            <div>
              <Label className="text-xs font-semibold">Khách Hàng</Label>
              <Select value={selectedCustomerId} onValueChange={setSelectedCustomerId}>
                <SelectTrigger className="mt-1 h-8 text-xs">
                  <SelectValue placeholder="Chọn khách hàng..." />
                </SelectTrigger>
                <SelectContent>
                  {customerList.map((c: any) => (
                    <SelectItem key={c.id || c.code} value={c.id || c.code} className="text-xs">
                      [{c.code || 'KH'}] {c.name || c.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-semibold">Tên Sản Phẩm Bao Bì</Label>
              <Input
                value={customProductName}
                onChange={e => setCustomProductName(e.target.value)}
                className="mt-1 h-8 text-xs font-semibold"
              />
            </div>

            <div className="p-3 bg-slate-50 rounded-lg border text-xs space-y-1.5 font-mono">
              <div className="flex justify-between">
                <span className="text-slate-500">Số lượng chi tiết:</span>
                <span className="font-bold">{components.length} thành phần</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Số lượng đặt:</span>
                <span className="font-bold">{targetOrderQty.toLocaleString()} bộ</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Đơn giá trọn bộ:</span>
                <span className="font-bold text-indigo-600">
                  {targetMachineKey === 'SMALL' ? small.unitPrice.toLocaleString() : large.unitPrice.toLocaleString()} đ / bộ
                </span>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">Ghi Chú</Label>
              <Input
                placeholder="Ghi chú điều khoản..."
                value={quoteNotes}
                onChange={e => setQuoteNotes(e.target.value)}
                className="mt-1 h-8 text-xs"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsQuoteModalOpen(false)}>
              Hủy
            </Button>
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
              onClick={handleConfirmCreateQuote}
              disabled={convertMutation.isPending}
            >
              {convertMutation.isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Đang khởi tạo...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                  Xác Nhận Tạo Báo Giá
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
}
